import { useMemo } from 'react'

import { usePortal } from '../../../../../../app/_providers/PortalProvider.client'
import { useTranslation } from '../../../../../../app/_providers/TranslationProvider.client'
import { CartContainer } from '../../../../../../brunswick/containers/cart'
import { EmbroideryService } from '../../../../../../brunswick/containers/embroidery'
import { EmbroideryModalService } from '../../../../../../brunswick/containers/embroidery/EmbroideryModalService'
import { Output as AddToBagOutput } from '../../../../../../lib/hooks/useBag'
import { hashPrefixed } from '../../../../../../lib/utils'

interface Props {
  isPortalColorSelected: boolean
  resetInvalidBagClickStatus: AddToBagOutput['resetInvalidBagClickStatus']
}

export function useViewModel({ isPortalColorSelected, resetInvalidBagClickStatus }: Props) {
  const {
    clearSelectedEmbroideryFormOptions,
    confirmAddEmbroidery,
    selectedEmbroideryFormOptions: options,
    toggleConfirmAddEmbroidery,
  } = EmbroideryService.useContainer()

  const { openEmbroideryModal } = EmbroideryModalService.useContainer()
  const {
    embroidery: { summary: strings, discountText },
  } = useTranslation()

  const { productColorHex, productCategory } = EmbroideryService.useContainer()

  const { cart } = CartContainer.useContainer()

  const { portal, hasMandatoryLogoEmbroidery, hasMandatoryTextEmbroidery } = usePortal()
  const { embroidery } = portal || {}

  const mandatoryLogoEmbroidery = hasMandatoryLogoEmbroidery(productCategory)
  const mandatoryTextEmbroidery = hasMandatoryTextEmbroidery(productCategory)

  const backgroundColor = hashPrefixed(productColorHex)

  const handleToggleConfirmAddEmbroidery = () => {
    if (isPortalColorSelected) {
      resetInvalidBagClickStatus()
    }
    toggleConfirmAddEmbroidery()
  }

  const setupSkuInCart = useMemo(() => {
    if (cart?.items) {
      return Object.values(cart.items).some(
        item =>
          item.properties?._is_logo_setup === 'true' && item.properties?._logo_id === options.logoId
      )
    }
    return false
  }, [cart, options.logoId])

  const embroideryOptionPrice = useMemo(() => {
    let price = options.embroideryItemVariant?.price ?? 0
    if (options.embroiderySetupVariant && (options.setupFile || !setupSkuInCart)) {
      price += options.embroiderySetupVariant.price
    }
    return price
  }, [options, setupSkuInCart])

  const shouldDisplayLogo = isPortalColorSelected
  return {
    clearSelectedEmbroideryFormOptions,
    confirmAddEmbroidery,
    editEmbroideryText: strings.editEmbroidery,
    embroideryOptionPrice: embroideryOptionPrice,
    openEmbroideryModal,
    toggleAddEmbroideryText: strings.toggleAddEmbroidery,
    toggleConfirmAddEmbroidery: handleToggleConfirmAddEmbroidery,
    textSneakPeekProps: {
      backgroundColor,
      textColor: options?.textColor,
      textFirstLine: options?.textFirstLine,
      textFont: options?.textFont,
      textPlacement: options?.textPlacement,
      textSecondLine: options?.textSecondLine,
    },
    iconSneakPeekProps: shouldDisplayLogo
      ? {}
      : {
          backgroundColor,
          iconImageSource: options?.iconImageSource,
          iconInitials: options?.iconInitials,
          iconInitialsColor: options?.iconInitialsColor,
          iconDesignName: options?.iconDesignName,
          iconPlacement: options?.iconPlacement,
        },
    dtcLogoSneakPeekProps: {
      backgroundColor,
      imgSrc: options?.logoPreview,
      logoName: options?.logoName,
      logoPlacement: options?.logoPlacement,
      logoColor: options?.logoColor,
      logoNote: options?.logoNote,
      hasLogoSetup: options?.embroiderySetupVariant && (options?.setupFile || !setupSkuInCart),
    },
    logoSneakPeekProps: shouldDisplayLogo
      ? {
          backgroundColor,
          imgSrc: options?.logoImageSource,
          logoName: options?.logoName,
          logoPlacement: options.logoPlacement,
        }
      : {},
    embroideryDiscountMessage:
      isPortalColorSelected && embroidery?.embroideryDiscountPercentage
        ? discountText.summarySection.replace(
            '{{discountPercentage}}',
            embroidery.embroideryDiscountPercentage.toString()
          )
        : undefined,
    canEditEmbroidery: isPortalColorSelected
      ? !mandatoryTextEmbroidery && !mandatoryLogoEmbroidery
      : true,
  }
}
